import React, { MouseEvent, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { getProductImageURL, ProductImageSize } from '@noths/polaris-client-ribbons-design-system';
import { useIntersection } from '@noths/polaris-client-utils';

import { TRANSITION_DURATION_IN_MS } from '../constants';
import * as styles from '../styles/PartnerCardImage.styles';

export type Props = {
  href: string;
  alt?: string;
  size?: ProductImageSize;
  srcSet?: string;
  sizes?: string;
  disableLazyLoading?: boolean;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
};

export const PartnerCardImage = ({
  href,
  size,
  alt = '',
  srcSet,
  sizes,
  disableLazyLoading = false,
  onClick,
}: Props) => {
  const placeholderRef = useRef<HTMLImageElement | null>(null);
  const [hasLoadedImage, setHasLoadedImage] = useState(false);
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);

  useIntersection(() => setHasScrolledIntoView(true), placeholderRef, {
    rootMargin: '0px 0px 1000px 0px',
  });

  const isVisible = hasLoadedImage || disableLazyLoading;

  /*
    Prevent image from jarringly popping into view, by rendering a placeholder before the image has loaded.
    Render product placeholder initially and then only render the actual image when the placeholder has been scrolled into view. fade out placeholder & fade in image after it has finished loading.
  */
  return (
    <>
      <CSSTransition in={!hasLoadedImage} timeout={TRANSITION_DURATION_IN_MS} unmountOnExit>
        <div
          data-testid="image-placeholder"
          ref={placeholderRef}
          css={[styles.image, styles.placeholder]}
          data-is-visible={!hasLoadedImage}
        />
      </CSSTransition>
      <CSSTransition
        in={hasScrolledIntoView || disableLazyLoading}
        timeout={TRANSITION_DURATION_IN_MS}
        unmountOnExit
      >
        <img
          css={styles.image}
          alt={alt}
          src={getProductImageURL(href, size)}
          srcSet={srcSet}
          sizes={sizes}
          data-is-visible={isVisible}
          onLoad={() => setHasLoadedImage(true)}
          {...(isVisible && { onClick })}
        />
      </CSSTransition>
    </>
  );
};
